/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { ThemeEvents } from '@theme/events';

/**
 * The locked pre-sale label (snippets/pre-sale-label.liquid) and the cart bar
 * (snippets/pre-sale-cart-notice.liquid) link to the login page. Where the
 * header's account dialog is on the page, a click opens that instead: it holds
 * the sign-in options and brings the customer back to the page they were on.
 *
 * A login started from the cart bar ends with the cart open again, now with the
 * pre-sale prices. Only that login: the marker the bar leaves is dropped when
 * the account dialog is dismissed, and used up on the next page load either way.
 *
 * The labels follow what the shopper picks: on the product page and in quick
 * add the selected variant, on product cards the selected variant or the
 * previewed colourway, which can also add or remove the card's label.
 */

const REOPEN_CART_KEY = 'pre-sale-reopen-cart';

/** A marker older than this is ignored, e.g. a login abandoned and finished much later. */
const REOPEN_CART_MAX_AGE_MS = 15 * 60 * 1000;

function isLoggedIn() {
  return document.querySelector('script[data-pre-sale-script]')?.hasAttribute('data-logged-in') ?? false;
}

function forgetReopenCart() {
  try {
    sessionStorage.removeItem(REOPEN_CART_KEY);
  } catch {
    // Storage unavailable: nothing was stored.
  }
}

async function reopenCartAfterLogin() {
  let startedAt = 0;

  try {
    startedAt = Number(sessionStorage.getItem(REOPEN_CART_KEY));
    sessionStorage.removeItem(REOPEN_CART_KEY);
  } catch {
    return;
  }

  if (!startedAt || Date.now() - startedAt > REOPEN_CART_MAX_AGE_MS || !isLoggedIn()) return;

  await customElements.whenDefined('cart-drawer-component');
  /** @type {any} */ (document.querySelector('cart-drawer-component'))?.open();
}

/**
 * Swaps the product page's pre-sale labels for the ones in the variant
 * response, which the variant picker fetches for the price anyway. Quick add
 * shows the product page details in a dialog outside any section.
 * @param {Event} event
 */
function refreshProductLabels(event) {
  const target = event.target;
  // Product cards hand their responses over through pre-sale:refresh instead.
  if (!(target instanceof Element) || target.closest('product-card')) return;

  const html = /** @type {CustomEvent} */ (event).detail?.data?.html;
  const scope = target.closest('quick-add-dialog') ?? target.closest('.shopify-section');
  if (!(html instanceof Document) || !scope) return;

  for (const label of scope.querySelectorAll('.pre-sale--product[data-product-id]')) {
    if (!(label instanceof HTMLElement)) continue;

    const fresh = html.querySelector(`.pre-sale--product[data-product-id="${label.dataset.productId}"]`);
    if (fresh) label.replaceWith(document.importNode(fresh, true));
  }
}

/**
 * A card label for a previewed colourway, from the page's label template
 * (snippets/header-actions.liquid) with that colourway's discount.
 * @param {number} percentage
 * @returns {Element | null}
 */
function cardLabelFromTemplate(percentage) {
  const template = document.getElementById('pre-sale-label-template');
  if (!(template instanceof HTMLTemplateElement)) return null;

  const label = template.content.querySelector('.pre-sale')?.cloneNode(true);
  if (!(label instanceof Element)) return null;

  const discount = label.querySelector('.pre-sale__discount');
  if (percentage > 0 && discount) {
    discount.textContent = (discount.textContent ?? '').replace('PRE_SALE_PERCENT', String(percentage));
  } else {
    discount?.remove();
  }

  return label;
}

/**
 * Replaces, adds or removes a product card's label, above the card title where
 * the title block renders it.
 * @param {Event} event - pre-sale:refresh from product-card.js (a variant response)
 *   or card-variation-hover.js (the previewed colourway's discount, null when untagged)
 */
function refreshCardLabel(event) {
  const card = event.target;
  if (!(card instanceof Element)) return;

  const { html, percentage } = /** @type {CustomEvent} */ (event).detail ?? {};
  /** @type {Element | null} */
  let label = null;

  if (html instanceof Document) {
    const fresh = html.querySelector('.pre-sale:not(.pre-sale--product)');
    label = fresh ? document.importNode(fresh, true) : null;
  } else if (percentage != null) {
    label = cardLabelFromTemplate(Number(percentage));
  }

  card.querySelector('.pre-sale:not(.pre-sale--product)')?.remove();
  if (label) card.querySelector('[ref="productTitleLink"], .resource-card__title')?.before(label);
}

if (!(/** @type {any} */ (window).__preSaleLabelInit)) {
  /** @type {any} */ (window).__preSaleLabelInit = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-pre-sale-login]') : null;
    if (!trigger) return;

    const accountDialog = /** @type {any} */ (document.querySelector('header-actions .account-dialog'));

    if (trigger.hasAttribute('data-pre-sale-reopen-cart')) {
      try {
        sessionStorage.setItem(REOPEN_CART_KEY, String(Date.now()));
      } catch {
        // Storage unavailable (private mode): the login simply doesn't reopen the cart.
      }
      // Dismissing the dialog ends this attempt; a later, ordinary login on the
      // same page must not reopen the cart. Signing in leaves the page instead.
      accountDialog?.addEventListener('dialog:close', forgetReopenCart, { once: true });
    }

    if (typeof accountDialog?.showDialog !== 'function') return;

    event.preventDefault();
    accountDialog.showDialog();
  });

  document.addEventListener(ThemeEvents.variantUpdate, refreshProductLabels);
  document.addEventListener('pre-sale:refresh', refreshCardLabel);

  reopenCartAfterLogin();
}
